/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  notification,
  Row,
  Tabs,
  TabsProps,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '../../../interfaces/dataModal';
import { IProduct } from '../../../interfaces/product';
import { IInboundItem } from '@/interfaces/inbound';
import { DetailItem } from '@/components/dataItem/data-view-item';
import InboundProductFinderTab from './inbound-product-finder-tab';
import ProductTableDetail from '@/components/product/product-table-detail';
import InboundProductCreate from './inbound-product-create';
import { updateInboundItem } from '@/adapters/inbound';
import ConfirmApprove from '@/components/modal/confirm-approve';

interface IProps {
  dataModal?: IDataModal<Partial<IInboundItem>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const _modal: IDataModal<IProduct> = {
  open: false,
};

const InboundProductModal = ({ dataModal, handleOk, handleCancel }: IProps) => {
  const [form] = Form.useForm<IProduct>();
  const [product, setProduct] = useState<IProduct[]>([]);
  const [completedOrder, setCompletedOrder] = useState<IDataModal<IProduct>>({
    ..._modal,
  });

  const [tabKey, setTabKey] = useState('product');

  const mutations = useMutation({
    mutationFn: async (data: Partial<IInboundItem>) =>
      await updateInboundItem(data),
    mutationKey: ['update-inbound-item'],
    onSuccess: async data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        const message = 'อัพเดทสินค้าสำเร็จ';
        notification.success({
          message: message,
        });
        setProduct([]);
        await setCompletedOrder({ ..._modal, open: false });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    await mutations.mutateAsync({
      ...data,
      productId: product[0].id,
      productCode: product[0].code,
      pdtDescriptionE: product[0].name,
      uom: product[0].baseUnitId,
      isSendInbound: product[0].isSendInbound,
    });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.id) {
      form.setFieldsValue({
        ...dataModal?.item,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  const data = dataModal?.item;

  const items: TabsProps['items'] = [
    {
      key: 'product',
      label: 'ค้นหาสินค้า',
      children: (
        <Suspense>
          <InboundProductFinderTab
            onSelectProduct={data => setProduct([data])}
          />
        </Suspense>
      ),
    },
    {
      key: 'new-product',
      label: 'สร้างสินค้าใหม่',
      children: (
        <Suspense>
          <InboundProductCreate
            handleOk={data => {
              setProduct([data]);
              setCompletedOrder({
                open: true,
              });
            }}
            data={data}
          />
        </Suspense>
      ),
    },
  ];

  return (
    <Modal
      title={'แก้ไข Invoice'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={1000}
      footer={
        tabKey == 'new-product'
          ? false
          : [
              <Button key="back" onClick={handleCancel}>
                ยกเลิก
              </Button>,
              <Button
                key="submit"
                type="primary"
                form="productGroup"
                disabled={!product.length}
                // loading={mutations.isPending}
                onClick={handleUpdate}
              >
                บันทึก
              </Button>,
            ]
      }
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="product"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
        >
          <Row gutter={12}>
            <Col span={24}>
              <div className="grid md:grid-cols-2">
                <DetailItem label="INV NUMBER" detail={data?.invNO} />
                <DetailItem label="ITEM NAME" detail={data?.pdtDescriptionE} />
              </div>
            </Col>
            <Col span={24}>
              <Tabs
                defaultActiveKey="product"
                items={items}
                className="w-full"
                rootClassName="font-medium"
                onChange={tabKey => {
                  setTabKey(tabKey);
                  setProduct([]);
                }}
              />
            </Col>
            <Col span={24}>
              <ProductTableDetail product={product || []} />
            </Col>
          </Row>
        </Form>
        <ConfirmApprove
          showCancel={false}
          content={`คุณต้องการยืนยันการสร้างสินค้า ใช่หรือไม่`}
          handleCancel={() => {
            setCompletedOrder({ ..._modal });
          }}
          handleOk={() => {
            setCompletedOrder({ ..._modal, open: false });
            handleUpdate();
          }}
          open={completedOrder?.open}
          title={`ยืนยัน`}
        />
      </div>
    </Modal>
  );
};

export default InboundProductModal;
